import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { MenuIcon } from "../../constants/menuIcons";
import LanguageSelector from "../common/LanguageSelector";
import { useTranslation } from "../../contexts/TranslationContext";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

// Mobile mega-menu (DoD 22) — full-screen panel below the mobile header with
// the primary nav as flat links, quick-access pills, and the same category
// data as the desktop mega-menu rendered as collapsible accordions (Marin's
// design). Data-driven from useHeaderNavigation; app idiom + platform blue.

const MegaMenuMobile = ({
  isOpen,
  onClose,
  anchorTop = 0,
  navigationItems = [],
  categories = [],
  pills = [],
  isActive,
}) => {
  // First category open by default so the panel doesn't look empty.
  const [expanded, setExpanded] = useState(() =>
    categories[0] ? { [categories[0].id]: true } : {},
  );
  // Item titles/descriptions translate via the runtime observer (nudged below);
  // the menu's own category labels translate synchronously at render.
  const { currentLanguage, translateNewContent } = useTranslation();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => translateNewContent && translateNewContent(), 120);
    return () => clearTimeout(id);
  }, [isOpen, translateNewContent]);

  if (!isOpen) return null;

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white overflow-auto flex flex-col"
      style={{ top: anchorTop }}
    >
      <div className="p-4 flex-1">
        {pills.length > 0 && (
          <div className="flex flex-wrap gap-2 pb-4 border-b border-[#EFF1F4]">
            {pills.map((pill) => {
              return (
                <Link
                  key={pill.id || pill.path}
                  to={pill.path}
                  onClick={onClose}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-full border text-[13px] font-semibold min-h-[38px] ${
                    pill.badge > 0 || pill.newPill
                      ? "border-[#BFDBFE] bg-[#E8F1FF] text-[#1B6FE0]"
                      : "border-gray-200 text-[#0F1419]"
                  }`}
                >
                  {pill.iconKey && (
                    <MenuIcon name={pill.iconKey} size={15} strokeWidth={2} className="flex-shrink-0" />
                  )}
                  <span>{tr(pill.label)}</span>
                  {pill.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#2283FF] text-white text-[11px] font-bold leading-none">
                      {pill.badge}
                    </span>
                  )}
                  {pill.newPill && (
                    <span className="rounded-full bg-[#2283FF] text-white text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 leading-none">
                      New
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Primary nav — flat links so the top-bar destinations stay reachable
            on mobile where they can't sit inline. */}
        {navigationItems.length > 0 && (
          <div className="flex flex-col py-2 border-b border-[#EFF1F4]">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`px-2 py-3 text-base rounded-md ${
                  isActive?.(item.path)
                    ? "text-[#2283FF] bg-[#EEF4FE]"
                    : "text-[#0F1419]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}

        {categories.map((cat) => (
          <div key={cat.id} className="border-b border-[#EFF1F4]">
            <button
              type="button"
              onClick={() => toggle(cat.id)}
              className="w-full flex items-center justify-between py-4 px-1.5 min-h-[44px]"
              aria-expanded={!!expanded[cat.id]}
            >
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                {tr(cat.label)}
              </span>
              <span className="flex items-center gap-2.5">
                <span className="text-xs font-semibold text-gray-400">
                  {cat.items.length}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    expanded[cat.id] ? "rotate-180" : ""
                  }`}
                />
              </span>
            </button>
            {expanded[cat.id] && (
              <div className="flex flex-col gap-0.5 pb-3">
                {cat.items.map((item) => {
                  const active = isActive?.(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={`flex gap-3 p-2.5 rounded-xl min-h-[44px] ${
                        active ? "bg-[#EEF4FE]" : ""
                      }`}
                    >
                      <MenuIcon
                        name={item.iconKey}
                        size={22}
                        className="flex-shrink-0 mt-0.5 text-[#374151]"
                      />
                      <div className="min-w-0">
                        <div
                          className={`flex items-center gap-2 text-[14.5px] font-semibold leading-snug ${
                            active ? "text-[#2283FF]" : "text-[#0F1419]"
                          }`}
                        >
                          {tr(item.title)}
                          {item.badge > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#2283FF] text-white text-[11px] font-bold leading-none">
                              {item.badge}
                            </span>
                          )}
                          {item.isNew && (
                            <span className="inline-flex items-center rounded-full bg-[#E8F1FF] text-[#1B6FE0] text-[10px] font-bold leading-none px-1.5 py-0.5 uppercase tracking-[0.04em]">
                              New
                            </span>
                          )}
                          {/* What's New unread indicator (D-003: a plain dot). */}
                          {item.dot && (
                            <span
                              className="w-2 h-2 rounded-full bg-[#2283FF] inline-block"
                              aria-label="New updates"
                            />
                          )}
                        </div>
                        {item.description && (
                          <div className="text-[12.5px] text-gray-500 leading-snug mt-0.5">
                            {tr(item.description)}
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Language selector kept from the old mobile menu (functionality
          unchanged — the compact "EN" in the design is the desktop form). */}
      <div className="flex items-center justify-center px-4 py-6 border-t border-[#EFF1F4]">
        <LanguageSelector />
      </div>
    </div>
  );
};

export default MegaMenuMobile;
